import React, { useMemo, useState, useCallback } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useQuery, useLazyQuery } from '@apollo/client'
import { Row, Col, Table, Form, List, Collapse, notification } from 'antd'
import { Pie, Bar } from '@ant-design/charts'
import {
  EyeOutlined,
  DownloadOutlined,
  ExceptionOutlined,
} from '@ant-design/icons'
import { map, size, isEmpty, values, includes, join } from 'lodash-es'
import { Button, Panel } from '../../../core/components/styled'
import { THEME } from '../../../core/constants'
import { formatDate } from '../../../core/utils/services/formatter'
import {
  timeBetween,
  formatSecondsToTime,
} from '../../../core/utils/services/date-utils'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { getAutoEatMenuUrl, getAutoEatDishReviewTaskUrl } from '../../helpers'
import {
  PageActions,
  PageHeader,
  PageTitle,
} from '../../../common/components/styled'
import { Spinner } from '../../../common/components'
import { useDocumentTitle } from '../../../common/hooks/effects'
import {
  SCRAPING_SESSION,
  SCRAPING_TEST_CASE_RESULTS,
  SCRAPING_SESSION_STATISTICS,
  SCRAPING_RESPONSE_REPORT_URL,
} from '../../graphql/queries'
import { generateFailedTestCasesReport } from '../../rest'
import {
  ScrapingSessionStatus,
  ScrapingResponseStatus,
  JsonViewModal,
  ScrapingSessionLogsModal,
} from './sections'
import {
  SCRAPING_RESPONSE_STATUS_IN_PROGRESS,
  SCRAPING_RESPONSE_STATUS_ERROR,
  SCRAPING_RESPONSE_STATUSES,
  SCRAPING_RESPONSE_TYPE_ERROR,
  PASSED,
  FAILED,
  SCRAPING_TAG_TYPES,
  SCRAPING_FLAGS,
  SCRAPING_TEST_CASE_RESULT_OUTCOMES,
  SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED,
  SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED,
  SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS,
} from '../../constants'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../common/constants'
import fileDownload from 'js-file-download'

const transformDataForBar = (countsByTagTypes = []) => {
  const countsByTagTypesWithZeroes = countsByTagTypes.reduce((result, item) => {
    const key = `${SCRAPING_TAG_TYPES[item.type].name} ${item.name}`

    const value = result.has(key)
      ? result.get(key)
      : {
          SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED: 0,
          SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED: 0,
          SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS: 0,
        }

    const newValue = { ...value, [item.outcome]: item.count }
    return result.set(key, newValue)
  }, new Map())

  const addItemToArray = (array, tag, outcome, value) =>
    array.push({ tag, outcome, value })

  return [...countsByTagTypesWithZeroes.keys()].reduce((result, key) => {
    const mapValue = countsByTagTypesWithZeroes.get(key)

    addItemToArray(
      result,
      key,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED,
      mapValue[SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED],
    )

    addItemToArray(
      result,
      key,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED,
      mapValue[SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED],
    )

    addItemToArray(
      result,
      key,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS,
      mapValue[SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS],
    )

    return result
  }, [])
}

const generateReport = async (sessionId, setIsGeneratingReport) => {
  try {
    setIsGeneratingReport(true)
    const reportReponse = await generateFailedTestCasesReport(sessionId)
    fileDownload(
      reportReponse.data,
      `FailedTestCasesReport_SessionId-${sessionId}.csv`,
    )
    setIsGeneratingReport(false)
  } catch (e) {
    notification.open({
      message: 'Failed to generate report.',
      description: e,
      type: 'error',
      duration: 0,
    })
  }
}

const ScrapingSessionPage = () => {
  useDocumentTitle('Scraping session')
  const history = useHistory()
  const { sessionId } = useParams()
  const { pathname, search } = useLocation()
  const { page = DEFAULT_PAGE, pageSize = 10 } = getQueryParams(search)

  const [jsonViewModalSettings, setJsonViewModalSettings] = useState({
    isModalOpen: false,
    title: '',
    data: {},
  })

  const [
    menuScrapingSessionLogsModalSettings,
    setMenuScrapingSessionLogsModalSettings,
  ] = useState({
    title: null,
    isModalOpen: false,
    menuId: null,
    sessionId: null,
  })

  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const [scrapingReportResponseId, setScrapingReportResponseId] = useState()

  const {
    data: { scrapingSession } = {},
    loading: isLoadingSession,
  } = useQuery(SCRAPING_SESSION, {
    variables: { id: sessionId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: { scrapingTestCaseResults = {} } = {},
    loading: isLoadingTestCaseResults,
  } = useQuery(SCRAPING_TEST_CASE_RESULTS, {
    variables: {
      scrapingSessionId: sessionId,
      page,
      pageSize,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const [
    fetchGraphsData,
    {
      data: { scrapingSessionStatistics: statistics = {} } = {},
      loading: isLoadingGraphsData,
    },
  ] = useLazyQuery(SCRAPING_SESSION_STATISTICS, {
    variables: { sessionId },
    notifyOnNetworkStatusChange: true,
  })

  const [
    getScrapingResponseReportUrl,
    {
      data: { scrapingResponseReportUrl } = {},
      loading: isGeneratingScrapingReportUrl,
    },
  ] = useLazyQuery(SCRAPING_RESPONSE_REPORT_URL, {
    onCompleted() {
      window.open(scrapingResponseReportUrl, '_blank')
    },
    onError({ message }) {
      notification.open({
        message: 'Download scraping report failed.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
    fetchPolicy: 'no-cache',
  })

  const handleDownloadScrapingReport = useCallback(
    scrapingResponse => () => {
      setScrapingReportResponseId(scrapingResponse.id)

      getScrapingResponseReportUrl({
        variables: {
          scrapingResponseId: scrapingResponse.id,
        },
      })
    },
    [getScrapingResponseReportUrl],
  )

  const tableColumns = [
    {
      title: 'Menu ID',
      dataIndex: ['scrapingTestCase', 'menuId'],
      width: '45px',
      ellipsis: true,
      render: menuId => (
        <a
          href={`${getAutoEatMenuUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {menuId}
        </a>
      ),
    },
    {
      title: 'DR Task URL',
      dataIndex: ['scrapingTestCase', 'menuId'],
      width: '60px',
      ellipsis: true,
      render: menuId => (
        <a
          href={`${getAutoEatDishReviewTaskUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Link
        </a>
      ),
    },
    {
      title: 'Menu URL',
      dataIndex: ['scrapingTestCase', 'menuUrl'],
      width: '140px',
      ellipsis: true,
      render: menuUrl => (
        <a href={menuUrl} target="_blank" rel="noopener noreferrer">
          {menuUrl}
        </a>
      ),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      width: '100px',
      filters: map(
        values(SCRAPING_TEST_CASE_RESULT_OUTCOMES),
        ({ name, value }) => ({
          text: name,
          value,
        }),
      ),
      onFilter: (outcomeType, scrapingTestCaseResult) =>
        scrapingTestCaseResult.outcome === outcomeType,
      render: outcome => outcome && <ScrapingSessionStatus status={outcome} />,
    },
    {
      title: 'Scraping status',
      dataIndex: 'scrapingResponse',
      width: '100px',
      filters: map(values(SCRAPING_RESPONSE_STATUSES), ({ name, value }) => ({
        text: name,
        value,
      })),
      onFilter: (outcomeType, scrapingTestCaseResult) => {
        if (outcomeType === SCRAPING_RESPONSE_STATUS_ERROR) {
          return (
            scrapingTestCaseResult?.scrapingResponse?.type ===
            SCRAPING_RESPONSE_TYPE_ERROR
          )
        }

        return (
          scrapingTestCaseResult?.scrapingResponse?.response?.status ===
          outcomeType
        )
      },
      render: scrapingResponse => {
        if (!scrapingResponse) {
          return (
            <ScrapingResponseStatus
              status={SCRAPING_RESPONSE_STATUS_IN_PROGRESS}
            />
          )
        }

        if (scrapingResponse.type === SCRAPING_RESPONSE_TYPE_ERROR) {
          return (
            <ScrapingResponseStatus
              status={SCRAPING_RESPONSE_STATUS_ERROR}
              code={scrapingResponse.response?.errorCode}
            />
          )
        }

        return (
          <ScrapingResponseStatus status={scrapingResponse.response.status} />
        )
      },
    },
    {
      title: 'Duration',
      dataIndex: ['scrapingResponse', 'response', 'duration'],
      width: '100px',
      render: durationInSeconds =>
        durationInSeconds && formatSecondsToTime(durationInSeconds),
    },
    {
      title: 'Menu annotation',
      width: '150px',
      dataIndex: ['scrapingResponse', 'response', 'menuAnnotations'],
      render: menuAnnotations =>
        map(menuAnnotations, ({ fullMenu }) => fullMenu).join(', '),
    },
    {
      title: 'Flags',
      width: '175px',
      dataIndex: ['scrapingResponse', 'response', 'flags'],
      filters: map(values(SCRAPING_FLAGS), ({ name, value }) => ({
        text: name,
        value,
      })),
      onFilter: (flag, scrapingTestCaseResult) =>
        includes(
          map(
            scrapingTestCaseResult?.scrapingResponse?.response?.flags,
            'text',
          ),
          flag,
        ),
      render: flags => join(map(flags, 'text'), ', '),
    },
    {
      title: 'Scraping instr.',
      width: '100px',
      dataIndex: ['scrapingTestCase', 'scrapingInstructions'],
      align: 'center',
      render: scrapingInstructions => (
        <Button
          icon={<EyeOutlined />}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Scraping instructions',
              data: scrapingInstructions,
            })
          }
        />
      ),
    },
    {
      title: 'Expected data',
      width: '100px',
      dataIndex: ['scrapingTestCase', 'scrapedData'],
      align: 'center',
      render: expectedData => (
        <Button
          icon={<EyeOutlined />}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Expected data',
              data: expectedData,
            })
          }
        />
      ),
    },
    {
      title: 'Scraped data',
      width: '100px',
      dataIndex: ['scrapingResponse', 'response'],
      align: 'center',
      render: scrapedData => (
        <Button
          icon={<EyeOutlined />}
          disabled={!scrapedData}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Scraped data',
              data: scrapedData,
            })
          }
        />
      ),
    },
    {
      title: 'New dishes',
      width: '80px',
      dataIndex: 'newDishes',
      render: (newDishes, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(newDishes)}
            onClick={() =>
              fileDownload(
                JSON.stringify(newDishes, null, 2),
                `${testCaseResult.id} - New_dishes.json`,
              )
            }
          >
            {size(newDishes)}
          </Button>
        ),
    },
    {
      title: 'Missing dishes',
      width: '85px',
      dataIndex: 'missingDishes',
      render: (missingDishes, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(missingDishes)}
            onClick={() =>
              fileDownload(
                JSON.stringify(missingDishes, null, 2),
                `${testCaseResult.id} - Missing_dishes.json`,
              )
            }
          >
            {size(missingDishes)}
          </Button>
        ),
    },
    {
      title: 'New menu titles',
      width: '85px',
      dataIndex: 'newMenuTitles',
      render: (newMenuTitles, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(newMenuTitles)}
            onClick={() =>
              fileDownload(
                JSON.stringify(newMenuTitles, null, 2),
                `${testCaseResult.id} - New_menu_titles.json`,
              )
            }
          >
            {size(newMenuTitles)}
          </Button>
        ),
    },
    {
      title: 'Missing menu titles',
      width: '85px',
      dataIndex: 'missingMenuTitles',
      render: (missingMenuTitles, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(missingMenuTitles)}
            onClick={() =>
              fileDownload(
                JSON.stringify(missingMenuTitles, null, 2),
                `${testCaseResult.id} - Missing_menu_titles.json`,
              )
            }
          >
            {size(missingMenuTitles)}
          </Button>
        ),
    },
    {
      title: 'Logs',
      width: '80px',
      align: 'center',
      dataIndex: ['scrapingResponse', 'response', 'correlationEvent'],
      render: correlationEvent => (
        <Button
          icon={<ExceptionOutlined />}
          disabled={!correlationEvent}
          onClick={() =>
            setMenuScrapingSessionLogsModalSettings({
              title: 'Menu scraping logs',
              menuId: correlationEvent.menuId,
              sessionId: correlationEvent.sessionId,
              isModalOpen: true,
            })
          }
        />
      ),
    },
    {
      title: 'Scraping exceptions',
      width: '110px',
      dataIndex: ['scrapingResponse', 'response', 'exceptions'],
      align: 'center',
      render: (exceptions, testCaseResult) => (
        <Button
          disabled={isEmpty(exceptions)}
          icon={<DownloadOutlined />}
          onClick={() =>
            fileDownload(
              JSON.stringify(exceptions, null, 2),
              `${testCaseResult.id} - Exceptions.json`,
            )
          }
        />
      ),
    },
    {
      title: 'Scraping Report',
      width: '110px',
      dataIndex: ['scrapingResponse'],
      align: 'center',
      render: scrapingResponse => (
        <Button
          disabled={!scrapingResponse?.response?.scrapingReport?.fileKey}
          icon={<DownloadOutlined />}
          loading={
            isGeneratingScrapingReportUrl &&
            scrapingResponse.id === scrapingReportResponseId
          }
          onClick={handleDownloadScrapingReport(scrapingResponse)}
        />
      ),
    },
  ]

  const getTestsCount = (list = [], outcome) =>
    (list.find(item => item.outcome === outcome) || {}).count || 0

  const getColor = outcome => {
    if (outcome === PASSED) {
      return THEME.colors.green
    }
    if (outcome === FAILED) {
      return THEME.colors.red
    }
    return THEME.colors.yellow
  }

  const pieConfig = useMemo(() => {
    const passedTestsCount = getTestsCount(
      statistics.scrapingTestCaseResultCountByOutcome,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED,
    )

    const failedTestsCount = getTestsCount(
      statistics.scrapingTestCaseResultCountByOutcome,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED,
    )

    const inProgressTestsCount = getTestsCount(
      statistics.scrapingTestCaseResultCountByOutcome,
      SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS,
    )

    const totalTests =
      passedTestsCount + failedTestsCount + inProgressTestsCount

    return {
      color: ({ outcome }) => getColor(outcome),
      data: [
        {
          outcome: SCRAPING_TEST_CASE_RESULT_OUTCOME_PASSED,
          value: passedTestsCount,
        },
        {
          outcome: SCRAPING_TEST_CASE_RESULT_OUTCOME_FAILED,
          value: failedTestsCount,
        },
        {
          outcome: SCRAPING_TEST_CASE_RESULT_OUTCOME_IN_PROGRESS,
          value: inProgressTestsCount,
        },
      ],
      angleField: 'value',
      colorField: 'outcome',
      height: 250,
      legend: false,
      radius: 1,
      innerRadius: 0.64,
      label: {
        type: 'inner',
        offset: '-50%',
        style: { textAlign: 'center' },
        autoRotate: false,
        content: '{value}',
      },
      statistic: {
        title: false,
        content: {
          style: {
            whiteSpace: 'pre-wrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          formatter: data =>
            data
              ? `${SCRAPING_TEST_CASE_RESULT_OUTCOMES[data.outcome].name}: ${
                  data.value
                }`
              : `Total: ${totalTests}`,
        },
      },
      interactions: [
        { type: 'element-selected' },
        { type: 'element-active' },
        { type: 'pie-statistic-active' },
      ],
    }
  }, [statistics])

  const barsConfig = useMemo(() => {
    const data = transformDataForBar(
      statistics.scrapingTestCaseResultCountByTag,
    )

    return {
      data,
      isGroup: true,
      legend: false,
      xField: 'value',
      yField: 'tag',
      seriesField: 'outcome',
      marginRatio: 0,
      color: ({ outcome }) => getColor(outcome),
    }
  }, [statistics])

  return isLoadingSession ? (
    <Spinner />
  ) : (
    <>
      <PageHeader>
        <PageTitle>Scraping session details</PageTitle>
        <PageActions>
          <Button
            type="primary"
            margin="no small no no"
            onClick={() => generateReport(sessionId, setIsGeneratingReport)}
            loading={isGeneratingReport}
          >
            Generate failed test cases report
          </Button>
          <Button
            margin="no small no no"
            onClick={() => history.push('/scraping/sessions/history')}
          >
            Go back
          </Button>
        </PageActions>
      </PageHeader>
      <Panel margin="small no">
        <Form layout="vertical">
          <Row gutter={36}>
            <Col>
              <Form.Item label="ID">{scrapingSession.id}</Form.Item>
            </Col>
            <Col>
              <Form.Item label="Status">
                <ScrapingSessionStatus status={scrapingSession.status} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Started by">
                {scrapingSession.createdBy?.name || 'System'}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Started at">
                {formatDate(scrapingSession.startedAt)}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Completed at">
                {formatDate(scrapingSession.completedAt)}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Duration">
                {scrapingSession.completedAt &&
                  timeBetween(
                    scrapingSession.startedAt,
                    scrapingSession.completedAt,
                  )}
              </Form.Item>
            </Col>
          </Row>

          <Collapse
            forceRender
            style={{ maxHeight: '350px', overflow: 'auto' }}
            onChange={() => fetchGraphsData()}
          >
            <Collapse.Panel header="Graphs" key="graphs">
              {isLoadingGraphsData ? (
                <Spinner />
              ) : (
                <Row gutter={24}>
                  <Col span={18}>
                    <Bar {...barsConfig} />
                  </Col>
                  <Col span={6}>
                    <Pie {...pieConfig} />
                  </Col>
                </Row>
              )}
            </Collapse.Panel>
          </Collapse>
        </Form>
      </Panel>
      <Table
        loading={isLoadingTestCaseResults}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingTestCaseResults.scrapingTestCaseResults}
        scroll={{ x: 3000 }}
        size="small"
        expandable={{
          expandedRowRender: testCaseResult => (
            <List
              style={{
                height: 400,
                overflow: 'auto',
                padding: '0 16px',
              }}
              dataSource={testCaseResult.scrapingResponse.response.messages}
              renderItem={message => <List.Item>{message.text}</List.Item>}
            />
          ),
          rowExpandable: testCaseResult =>
            !isEmpty(testCaseResult.scrapingResponse?.response.messages),
        }}
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          showSizeChanger: false,
          total: scrapingTestCaseResults.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
        }}
      />

      <ScrapingSessionLogsModal
        {...menuScrapingSessionLogsModalSettings}
        closeModal={() =>
          setMenuScrapingSessionLogsModalSettings({
            title: null,
            menuId: null,
            scrapingSessionId: null,
            isModalOpen: null,
          })
        }
      />

      <JsonViewModal
        {...jsonViewModalSettings}
        closeModal={() =>
          setJsonViewModalSettings({
            isModalOpen: false,
            data: {},
            title: '',
          })
        }
      />
    </>
  )
}

export default ScrapingSessionPage
